import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const GATE_KEY = "qr_site_access";

export default function SiteGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(GATE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (unlocked) return children;

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/site-access", { code: code.trim() });
      try {
        localStorage.setItem(GATE_KEY, "1");
      } catch {
        /* ignore */
      }
      setUnlocked(true);
    } catch (err) {
      setError(errMsg(err) || "Incorrect access code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="site-gate"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#001a4d] px-4 text-white"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,rgba(0,82,255,0.4),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_82%_88%,rgba(0,82,255,0.28),transparent_52%)]" />
      <div className="relative w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-xl">
          <div className="mb-6 flex flex-col items-center text-center">
            <img
              src="/brand/quickride-logo.png"
              alt="Quick Ride"
              className="mb-5 h-11 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div className="mb-4 grid h-12 w-12 place-items-center rounded-full bg-[#0052FF]/20 ring-1 ring-[#0052FF]/40">
              <Lock className="h-5 w-5 text-[#6f9bff]" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Private testing</h1>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              Quick Ride is in private testing and isn&apos;t open to the public yet. Enter the
              access code to continue.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <Input
              data-testid="site-gate-code-input"
              autoFocus
              type="password"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Access code"
              className="h-12 border-white/15 bg-white/10 text-center text-lg tracking-[0.35em] text-white placeholder:tracking-normal placeholder:text-white/40 focus-visible:ring-[#0052FF]"
            />
            {error && (
              <p data-testid="site-gate-error" className="text-center text-sm text-rose-300">
                {error}
              </p>
            )}
            <Button
              data-testid="site-gate-submit"
              type="submit"
              disabled={busy}
              className="h-12 w-full bg-[#0052FF] text-base font-medium hover:bg-[#0047db]"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Unlock site"}
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-white/40">
            A GPRTU service · Developed &amp; operated by Nacasky Co. Ltd
          </p>
        </div>
      </div>
    </div>
  );
}
